<template>
  <div style="overflow-x: auto">
    <table class="error-table w-full min-w-min">
      <thead>
        <tr>
          <th>{{ $t('documentation.api.errors.table.code') }}</th>
          <th>{{ $t('documentation.api.errors.table.information') }}</th>
          <th v-if="hasLinkColumn">{{ $t('documentation.api.errors.table.actions') }}</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="error in errors" :id="error.code" :key="error.code">
          <td class="text-center">
            <link-wrapper v-if="errorPageUrl" :href="`${errorPageUrl}#${error.code}`">
              {{ error.code }}
            </link-wrapper>
            <code v-else class="error-codes bg-[#f2f2f2] text-[#bb3700] py-[3px] px-1 rounded-md">{{
              error.code
            }}</code>
          </td>

          <td v-if="error.resolution" class="pb-4">
            <p class="subtitle text-xs leading-[10px] font-extrabold tracking-[1px] uppercase pb-4">
              {{ $t('documentation.api.errors.table.description') }}
            </p>
            <html-text tag="span" :html="error.description" />
            <p class="subtitle text-xs leading-[10px] font-extrabold tracking-[1px] uppercase pb-4">
              {{ $t('documentation.api.errors.table.resolution') }}
            </p>
            <html-text tag="span" :html="error.resolution" />
          </td>
          <td v-else>
            <html-text tag="span" :html="error.description" />
          </td>

          <td v-if="hasLinkColumn" class="text-center max-w-[100%] whitespace-nowrap">
            <link-wrapper v-if="error.link" :href="error.link.url" type="button">
              <v-button
                stacked
                dense
                appearance="filled"
                :label="error.link.text"
                class="button-width"
                size="condensed"
              />
            </link-wrapper>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { Errors } from '@bifrost-dev-docs-mfe/common/types/apiDocs';
import { VButton } from '@vonage/vivid-vue';
import HtmlText from '@/components/utils/HtmlText.vue';
import LinkWrapper from '@/components/markdown/widgets/LinkWrapper.vue';

const props = defineProps({
  errorPageUrl: { type: String },
  errors: { type: Array as PropType<Errors>, required: true },
  showActions: { type: Boolean, default: false },
});

const hasLinkColumn = computed(() => props.showActions && props.errors.some(error => error.link));
</script>

<style scoped lang="scss">
tbody {
  p {
    margin: 0 !important;
  }
}
.subtitle {
  &:not(:first-child) {
    padding-top: 8px;
  }
}
.button-width {
  display: block;
}
.error-table {
  word-wrap: break-word;
  thead tr {
    border-bottom-color: var(--vvd-color-canvas-text);
  }
  tr {
    border-bottom-width: 1px;
    border-bottom-style: solid;
    block-size: 44px;
    border-bottom-color: var(--vvd-color-neutral-100);
  }
  td {
    padding: 12px 16px;
  }
}
</style>
